import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
} from "recharts";
import { Pencil, Plus, Trash2, Users, Package, Activity as ActivityIcon, Gift, BarChart3, CalendarCheck2, FlaskConical, Sparkles, BookOpen, Database, PlayCircle, Shield, Megaphone, ShieldCheck, Inbox, Search, Mail, Globe, Share2, TrendingUp, Mic } from "lucide-react";
import { lazy, Suspense } from "react";
import { TransformationsTab, ArticlesTab } from "./admin/ContentTabs";
// Heavy admin tabs lazy-loaded — drops initial /admin bundle ~40%.
const BackupTab = lazy(() => import("./admin/BackupTab"));
const HeroTab = lazy(() => import("./admin/HeroTab"));
const SecurityTab = lazy(() => import("./admin/SecurityTab"));
const CtaTab = lazy(() => import("./admin/CtaTab"));
const TwoFATab = lazy(() => import("./admin/TwoFATab"));
const SupportTab = lazy(() => import("./admin/SupportTab"));
const SeoTab = lazy(() => import("./admin/SeoTab"));
const EngagementTab = lazy(() => import("./admin/EngagementTab"));
const TranslationsTab = lazy(() => import("./admin/TranslationsTab"));
const SocialTab = lazy(() => import("./admin/SocialTab"));
const AnalyticsSnapshotCard = lazy(() => import("../components/admin/AnalyticsSnapshotCard"));
const AISearchPane = lazy(() => import("../components/admin/AISearchPane"));
const ShopV2Tab = lazy(() => import("../components/admin/shop/ShopV2Tab"));
const AuthorityTab = lazy(() => import("../components/admin/AuthorityTab"));
const VoiceStudioTab = lazy(() => import("./admin/VoiceStudioTab"));
import ImageUploadField from "../components/admin/ImageUploadField";
import ExperimentsHotBar from "../components/ExperimentsHotBar";

// Suspense fallback used inside the lazy admin tabs.
const TabFallback = () => (
  <div className="p-10 text-center text-emerald-700/70 text-sm" data-testid="admin-tab-loading">
    Loading…
  </div>
);

const emptyProduct = {
  name: "",
  description: "",
  benefits: "",
  tags: "",
  purchase_link: "",
  usage: "",
  image_url: "",
  price: "",
  price_gbp: "",
  allows_subscription: true,
  // Pricing + promo flexibility (2026-02-12)
  subscription_price_gbp: "",
  discount_percent: 10,
  promo_mode: "percent",  // "percent" | "gift" | "none"
  gift_title: "",
  gift_image_url: "",
  gift_enabled: false,
};

const emptyCoupon = {
  code: "",
  type: "percent",
  value: 10,
  kind: "promo",
  max_redemptions: 0,
  description: "",
  active: true,
};

function CdnStatusPill() {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    api
      .get("/admin/uploads/status")
      .then((r) => setStatus(r.data))
      .catch(() => setStatus(null));
  }, []);
  if (!status) return null;
  const onCdn = !!status.cloudinary_configured;
  return (
    <div
      className={`shrink-0 inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] font-semibold ${
        onCdn
          ? "border-emerald-200 bg-emerald-50 text-emerald-800"
          : "border-amber-200 bg-amber-50 text-amber-800"
      }`}
      title={
        onCdn
          ? "Uploads are stored on Cloudinary CDN — fast, persistent across deploys."
          : "Cloudinary not configured. Uploads fall back to local storage. Add CLOUDINARY_* env vars and redeploy to enable CDN."
      }
      data-testid="admin-cdn-status-pill"
    >
      <span className="hidden sm:inline">{onCdn ? "● Media on CDN" : "● Media: local fallback"}</span>
      <span className="sm:hidden">{onCdn ? "● CDN" : "● Local"}</span>
    </div>
  );
}

export default function AdminPage() {
  return (
    <main className="bg-leaf-pattern min-h-[calc(100vh-4rem)] py-10" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-semibold">
              Admin panel
            </div>
            <h1 className="font-display text-3xl sm:text-4xl text-emerald-950 font-light tracking-tight mt-1">
              Manage products, users & engagement.
            </h1>
          </div>
          <CdnStatusPill />
        </div>

        <Tabs defaultValue="analytics" className="w-full">
          <TabsList className="bg-white border border-emerald-100 rounded-full p-1 flex flex-wrap gap-1 h-auto">
            <TabsTrigger value="analytics" className="rounded-full data-[state=active]:bg-emerald-700 data-[state=active]:text-white px-5" data-testid="admin-tab-analytics">
              <ActivityIcon className="w-4 h-4 mr-2" /> Analytics
            </TabsTrigger>
            <TabsTrigger value="products" className="rounded-full data-[state=active]:bg-emerald-700 data-[state=active]:text-white px-5" data-testid="admin-tab-products">
              <Package className="w-4 h-4 mr-2" /> Products
            </TabsTrigger>
            <TabsTrigger value="shop_v2" className="rounded-full data-[state=active]:bg-emerald-700 data-[state=active]:text-white px-5" data-testid="admin-tab-shop-v2">
              <Package className="w-4 h-4 mr-2" /> Shop
            </TabsTrigger>
            <TabsTrigger value="users" className="rounded-full data-[state=active]:bg-emerald-700 data-[state=active]:text-white px-5" data-testid="admin-tab-users">
              <Users className="w-4 h-4 mr-2" /> Users
            </TabsTrigger>
            <TabsTrigger value="coupons" className="rounded-full data-[state=active]:bg-emerald-700 data-[state=active]:text-white px-5" data-testid="admin-tab-coupons">
              <Gift className="w-4 h-4 mr-2" /> Coupons
            </TabsTrigger>
            <TabsTrigger value="referrals" className="rounded-full data-[state=active]:bg-emerald-700 data-[state=active]:text-white px-5" data-testid="admin-tab-referrals">
              <BarChart3 className="w-4 h-4 mr-2" /> Referrals
            </TabsTrigger>
            <TabsTrigger value="checkins" className="rounded-full data-[state=active]:bg-emerald-700 data-[state=active]:text-white px-5" data-testid="admin-tab-checkins">
              <CalendarCheck2 className="w-4 h-4 mr-2" /> Check-ins
            </TabsTrigger>
            <TabsTrigger value="experiments" className="rounded-full data-[state=active]:bg-emerald-700 data-[state=active]:text-white px-5" data-testid="admin-tab-experiments">
              <FlaskConical className="w-4 h-4 mr-2" /> Experiments
            </TabsTrigger>
            <TabsTrigger value="transformations" className="rounded-full data-[state=active]:bg-emerald-700 data-[state=active]:text-white px-5" data-testid="admin-tab-transformations">
              <Sparkles className="w-4 h-4 mr-2" /> Stories
            </TabsTrigger>
            <TabsTrigger value="articles" className="rounded-full data-[state=active]:bg-emerald-700 data-[state=active]:text-white px-5" data-testid="admin-tab-articles">
              <BookOpen className="w-4 h-4 mr-2" /> Library
            </TabsTrigger>
            <TabsTrigger value="hero" className="rounded-full data-[state=active]:bg-emerald-700 data-[state=active]:text-white px-5" data-testid="admin-tab-hero">
              <PlayCircle className="w-4 h-4 mr-2" /> Hero
            </TabsTrigger>
            <TabsTrigger value="cta" className="rounded-full data-[state=active]:bg-emerald-700 data-[state=active]:text-white px-5" data-testid="admin-tab-cta">
              <Megaphone className="w-4 h-4 mr-2" /> CTA
            </TabsTrigger>
            <TabsTrigger value="backup" className="rounded-full data-[state=active]:bg-emerald-700 data-[state=active]:text-white px-5" data-testid="admin-tab-backup">
              <Database className="w-4 h-4 mr-2" /> Backup
            </TabsTrigger>
            <TabsTrigger value="security" className="rounded-full data-[state=active]:bg-emerald-700 data-[state=active]:text-white px-5" data-testid="admin-tab-security">
              <Shield className="w-4 h-4 mr-2" /> Security
            </TabsTrigger>
            <TabsTrigger value="support" className="rounded-full data-[state=active]:bg-emerald-700 data-[state=active]:text-white px-5" data-testid="admin-tab-support">
              <Inbox className="w-4 h-4 mr-2" /> Support
            </TabsTrigger>
            <TabsTrigger value="seo" className="rounded-full data-[state=active]:bg-emerald-700 data-[state=active]:text-white px-5" data-testid="admin-tab-seo">
              <Search className="w-4 h-4 mr-2" /> SEO
            </TabsTrigger>
            <TabsTrigger value="authority" className="rounded-full data-[state=active]:bg-emerald-700 data-[state=active]:text-white px-5" data-testid="admin-tab-authority">
              <TrendingUp className="w-4 h-4 mr-2" /> Authority
            </TabsTrigger>
            <TabsTrigger value="engagement" className="rounded-full data-[state=active]:bg-emerald-700 data-[state=active]:text-white px-5" data-testid="admin-tab-engagement">
              <Mail className="w-4 h-4 mr-2" /> Engagement
            </TabsTrigger>
            <TabsTrigger value="social" className="rounded-full data-[state=active]:bg-emerald-700 data-[state=active]:text-white px-5" data-testid="admin-tab-social">
              <Share2 className="w-4 h-4 mr-2" /> Social
            </TabsTrigger>
            <TabsTrigger value="translations" className="rounded-full data-[state=active]:bg-emerald-700 data-[state=active]:text-white px-5" data-testid="admin-tab-translations">
              <Globe className="w-4 h-4 mr-2" /> Translations
            </TabsTrigger>
            <TabsTrigger value="twofa" className="rounded-full data-[state=active]:bg-emerald-700 data-[state=active]:text-white px-5" data-testid="admin-tab-twofa">
              <ShieldCheck className="w-4 h-4 mr-2" /> 2FA
            </TabsTrigger>
            <TabsTrigger value="voice" className="rounded-full data-[state=active]:bg-emerald-700 data-[state=active]:text-white px-5" data-testid="admin-tab-voice">
              <Mic className="w-4 h-4 mr-2" /> Voice Studio
            </TabsTrigger>
          </TabsList>

          <TabsContent value="analytics" className="mt-6">
            <AnalyticsTab />
          </TabsContent>
          <TabsContent value="products" className="mt-6">
            <ProductsTab />
          </TabsContent>
          <TabsContent value="shop_v2" className="mt-6">
            <Suspense fallback={<TabFallback />}><ShopV2Tab /></Suspense>
          </TabsContent>
          <TabsContent value="users" className="mt-6">
            <UsersTab />
          </TabsContent>
          <TabsContent value="coupons" className="mt-6">
            <CouponsTab />
          </TabsContent>
          <TabsContent value="referrals" className="mt-6">
            <ReferralsTab />
          </TabsContent>
          <TabsContent value="checkins" className="mt-6">
            <CheckinsTab />
          </TabsContent>
          <TabsContent value="experiments" className="mt-6">
            <ExperimentsHotBar />
          </TabsContent>
          <TabsContent value="transformations" className="mt-6">
            <TransformationsTab />
          </TabsContent>
          <TabsContent value="articles" className="mt-6">
            <ArticlesTab />
          </TabsContent>
          <TabsContent value="hero" className="mt-6">
            <Suspense fallback={<TabFallback />}><HeroTab /></Suspense>
          </TabsContent>
          <TabsContent value="cta" className="mt-6">
            <Suspense fallback={<TabFallback />}><CtaTab /></Suspense>
          </TabsContent>
          <TabsContent value="backup" className="mt-6">
            <Suspense fallback={<TabFallback />}><BackupTab /></Suspense>
          </TabsContent>
          <TabsContent value="security" className="mt-6">
            <Suspense fallback={<TabFallback />}><SecurityTab /></Suspense>
          </TabsContent>
          <TabsContent value="support" className="mt-6">
            <Suspense fallback={<TabFallback />}><SupportTab /></Suspense>
          </TabsContent>
          <TabsContent value="seo" className="mt-6">
            <Suspense fallback={<TabFallback />}><SeoTab /></Suspense>
          </TabsContent>
          <TabsContent value="authority" className="mt-6">
            <Suspense fallback={<TabFallback />}><AuthorityTab /></Suspense>
          </TabsContent>
          <TabsContent value="engagement" className="mt-6">
            <Suspense fallback={<TabFallback />}><EngagementTab /></Suspense>
          </TabsContent>
          <TabsContent value="social" className="mt-6">
            <Suspense fallback={<TabFallback />}><SocialTab /></Suspense>
          </TabsContent>
          <TabsContent value="translations" className="mt-6">
            <Suspense fallback={<TabFallback />}><TranslationsTab /></Suspense>
          </TabsContent>
          <TabsContent value="twofa" className="mt-6">
            <Suspense fallback={<TabFallback />}><TwoFATab /></Suspense>
          </TabsContent>
          <TabsContent value="voice" className="mt-6">
            <Suspense fallback={<TabFallback />}><VoiceStudioTab /></Suspense>
          </TabsContent>
        </Tabs>
      </div>
    </main>
  );
}

function AnalyticsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await api.get("/admin/analytics");
        setData(d);
      } catch {
        toast.error("Failed to load analytics");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="text-emerald-700" data-testid="analytics-loading">Loading analytics…</div>;
  if (!data) return null;

  const eventRows = Object.entries(data.events_by_type || {}).map(([k, v]) => ({ name: k.replace("_", " "), count: v }));
  const productRows = (data.top_products_by_clicks || []).map((p) => ({
    name: p.product.name,
    count: p.clicks,
  }));
  const condRows = (data.top_conditions || []).map((c) => ({
    name: c.condition.replace(/_/g, " "),
    count: c.count,
  }));

  const stats = [
    { label: "Users", value: data.total_users, testid: "stat-users" },
    { label: "Assessments", value: data.total_assessments, testid: "stat-assessments" },
    { label: "Products", value: data.total_products, testid: "stat-products" },
    {
      label: "Clicks",
      value: Object.values(data.events_by_type || {}).reduce((a, b) => a + b, 0),
      testid: "stat-clicks",
    },
  ];

  return (
    <div className="space-y-8">
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="bg-white border border-emerald-100 rounded-2xl p-1.5" data-testid="analytics-inner-tabs">
          <TabsTrigger value="overview" className="data-[state=active]:bg-emerald-700 data-[state=active]:text-white rounded-xl px-4" data-testid="analytics-inner-tab-overview">
            Overview
          </TabsTrigger>
          <TabsTrigger value="ai-search" className="data-[state=active]:bg-emerald-700 data-[state=active]:text-white rounded-xl px-4" data-testid="analytics-inner-tab-ai-search">
            AI Search
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6 space-y-8">
          {/* Today's 30-second growth overview — top of every Analytics session. */}
          <Suspense fallback={<div className="text-emerald-700 text-sm">Loading snapshot…</div>}>
            <AnalyticsSnapshotCard />
          </Suspense>

          <div className="space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {stats.map((s) => (
                <Card key={s.label} className="rounded-2xl border-emerald-100" data-testid={s.testid}>
                  <CardContent className="p-5">
                    <div className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-semibold">
                      {s.label}
                    </div>
                    <div className="font-display text-3xl text-emerald-950 mt-2">{s.value ?? 0}</div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              <ChartCard title="Engagement events" rows={eventRows} testid="chart-events" />
              <ChartCard title="Top products by clicks" rows={productRows} testid="chart-top-products" />
            </div>
            <ChartCard title="Top health conditions" rows={condRows} testid="chart-conditions" />
          </div>
        </TabsContent>

        <TabsContent value="ai-search" className="mt-6">
          <Suspense fallback={<div className="text-emerald-700 text-sm">Loading AI search authority…</div>}>
            <AISearchPane />
          </Suspense>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ChartCard({ title, rows, testid }) {
  const palette = ["#047857", "#fbbf24", "#10b981", "#f59e0b", "#059669"];
  return (
    <Card className="rounded-2xl border-emerald-100" data-testid={testid}>
      <CardContent className="p-5">
        <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold mb-3">
          {title}
        </div>
        {rows.length === 0 ? (
          <div className="text-sm text-emerald-900/60 py-8 text-center">No data yet.</div>
        ) : (
          <div style={{ width: "100%", height: 260, minHeight: 260 }}>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={rows} margin={{ top: 5, right: 12, left: -20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#d1fae5" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#064e3b" }} interval={0} angle={-10} textAnchor="end" height={50} />
                <YAxis tick={{ fontSize: 11, fill: "#064e3b" }} allowDecimals={false} />
                <Tooltip cursor={{ fill: "rgba(4,120,87,0.08)" }} />
                <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                  {rows.map((_, idx) => (
                    <Cell key={idx} fill={palette[idx % palette.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ProductsTab() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyProduct);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/products");
      setProducts(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyProduct);
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name,
      description: p.description,
      benefits: (p.benefits || []).join(", "),
      tags: (p.tags || []).join(", "),
      purchase_link: p.purchase_link || "",
      usage: p.usage || "",
      image_url: p.image_url || "",
      price: p.price || "",
      price_gbp: p.price_gbp ?? "",
      allows_subscription: p.allows_subscription ?? true,
      subscription_price_gbp: p.subscription_price_gbp ?? "",
      discount_percent: p.discount_percent ?? 10,
      promo_mode: p.promo_mode || "percent",
      gift_title: p.gift_title || "",
      gift_image_url: p.gift_image_url || "",
      gift_enabled: !!p.gift_enabled,
    });
    setDialogOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      name: form.name,
      description: form.description,
      benefits: form.benefits.split(",").map((s) => s.trim()).filter(Boolean),
      tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean),
      purchase_link: form.purchase_link,
      usage: form.usage,
      image_url: form.image_url,
      price: form.price,
      price_gbp: Number(form.price_gbp) || 0,
      allows_subscription: !!form.allows_subscription,
      subscription_price_gbp: Number(form.subscription_price_gbp) || 0,
      discount_percent: Math.max(0, Math.min(90, Number(form.discount_percent) || 0)),
      promo_mode: ["percent", "gift", "none"].includes(form.promo_mode) ? form.promo_mode : "percent",
      gift_title: form.gift_title || "",
      gift_image_url: form.gift_image_url || "",
      gift_enabled: !!form.gift_enabled,
    };
    try {
      if (editing) {
        await api.put(`/products/${editing.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product created");
      }
      setDialogOpen(false);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete ${p.name}?`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Deleted");
      await load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <Card className="rounded-2xl border-emerald-100" data-testid="admin-products-card">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg text-emerald-950">Products</h2>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                onClick={openCreate}
                className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white"
                data-testid="admin-add-product-btn"
              >
                <Plus className="w-4 h-4 mr-1" /> Add product
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl" data-testid="admin-product-dialog">
              <DialogHeader>
                <DialogTitle>{editing ? "Edit product" : "New product"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={save} className="space-y-3">
                <div>
                  <Label>Name</Label>
                  <Input
                    required
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    className="rounded-xl"
                    data-testid="product-form-name"
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label>Price (display, e.g. £89)</Label>
                    <Input
                      value={form.price}
                      onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
                      className="rounded-xl"
                      placeholder="£89"
                      data-testid="product-form-price"
                    />
                  </div>
                  <div>
                    <Label>Price GBP (numeric, charged amount)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={form.price_gbp}
                      onChange={(e) => setForm((f) => ({ ...f, price_gbp: e.target.value }))}
                      className="rounded-xl"
                      placeholder="89.00"
                      data-testid="product-form-price-gbp"
                    />
                  </div>
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea
                    required
                    rows={3}
                    value={form.description}
                    onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                    className="rounded-xl"
                    data-testid="product-form-description"
                  />
                </div>
                <div>
                  <Label>Benefits (comma separated)</Label>
                  <Textarea
                    rows={2}
                    value={form.benefits}
                    onChange={(e) => setForm((f) => ({ ...f, benefits: e.target.value }))}
                    className="rounded-xl"
                    placeholder="Reduces cravings, Supports metabolism"
                    data-testid="product-form-benefits"
                  />
                </div>
                <div>
                  <Label>Tags (comma separated)</Label>
                  <Input
                    value={form.tags}
                    onChange={(e) => setForm((f) => ({ ...f, tags: e.target.value }))}
                    className="rounded-xl"
                    placeholder="weight_loss, metabolic_health, feel_great"
                    data-testid="product-form-tags"
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label>Purchase link</Label>
                    <Input
                      value={form.purchase_link}
                      onChange={(e) => setForm((f) => ({ ...f, purchase_link: e.target.value }))}
                      className="rounded-xl"
                      placeholder="https://..."
                      data-testid="product-form-link"
                    />
                  </div>
                  <div>
                    <ImageUploadField
                      label="Product image"
                      value={form.image_url}
                      onChange={(v) => setForm((f) => ({ ...f, image_url: v }))}
                      testid="product-form-image"
                      bucket="products"
                      helperText="Tip: square 1:1 photos render best on the dashboard cards."
                    />
                  </div>
                </div>
                <div>
                  <Label>Usage guidance</Label>
                  <Textarea
                    rows={2}
                    value={form.usage}
                    onChange={(e) => setForm((f) => ({ ...f, usage: e.target.value }))}
                    className="rounded-xl"
                    data-testid="product-form-usage"
                  />
                </div>

                {/* === Pricing + promo controls === */}
                <div className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-4 space-y-3" data-testid="product-pricing-card">
                  <div className="text-[11px] uppercase tracking-[0.18em] text-emerald-700 font-semibold">
                    Pricing & promotion
                  </div>
                  <div className="grid sm:grid-cols-3 gap-3">
                    <div>
                      <Label>One-time (£)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min={0}
                        value={form.price_gbp}
                        onChange={(e) => setForm((f) => ({ ...f, price_gbp: e.target.value }))}
                        className="rounded-xl"
                        data-testid="product-form-price-gbp"
                      />
                    </div>
                    <div>
                      <Label>Subscription (£)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min={0}
                        value={form.subscription_price_gbp}
                        onChange={(e) => setForm((f) => ({ ...f, subscription_price_gbp: e.target.value }))}
                        className="rounded-xl"
                        placeholder="0 = auto (one-time × discount)"
                        data-testid="product-form-subscription-price"
                      />
                    </div>
                    <div>
                      <Label>Discount %</Label>
                      <Input
                        type="number"
                        min={0}
                        max={90}
                        value={form.discount_percent}
                        onChange={(e) => setForm((f) => ({ ...f, discount_percent: e.target.value }))}
                        className="rounded-xl"
                        data-testid="product-form-discount-percent"
                      />
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { key: "percent", label: "Save %" },
                      { key: "gift", label: "Free gift" },
                      { key: "none", label: "No promo" },
                    ].map((m) => (
                      <button
                        key={m.key}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, promo_mode: m.key }))}
                        className={`text-xs rounded-full px-4 py-1.5 border transition ${
                          form.promo_mode === m.key
                            ? "bg-emerald-700 text-white border-emerald-700"
                            : "bg-white text-emerald-800 border-emerald-200 hover:bg-emerald-50"
                        }`}
                        data-testid={`product-promo-${m.key}`}
                      >
                        {m.label}
                      </button>
                    ))}
                  </div>
                  {form.promo_mode === "gift" && (
                    <div className="grid sm:grid-cols-2 gap-3 pt-1" data-testid="product-gift-fields">
                      <div className="sm:col-span-2 flex items-center gap-2">
                        <input
                          id="gift_enabled"
                          type="checkbox"
                          checked={!!form.gift_enabled}
                          onChange={(e) => setForm((f) => ({ ...f, gift_enabled: e.target.checked }))}
                          data-testid="product-gift-enabled"
                        />
                        <Label htmlFor="gift_enabled" className="text-sm cursor-pointer">
                          Show this gift on the subscription card
                        </Label>
                      </div>
                      <div>
                        <Label>Gift title</Label>
                        <Input
                          value={form.gift_title}
                          onChange={(e) => setForm((f) => ({ ...f, gift_title: e.target.value }))}
                          placeholder="Free shaker bottle"
                          className="rounded-xl"
                          data-testid="product-form-gift-title"
                        />
                      </div>
                      <div>
                        <ImageUploadField
                          label="Gift image (optional)"
                          value={form.gift_image_url}
                          onChange={(v) => setForm((f) => ({ ...f, gift_image_url: v }))}
                          testid="product-form-gift-image"
                          bucket="products"
                          helperText="Square photo of the bonus item."
                        />
                      </div>
                    </div>
                  )}
                  <label className="flex items-center gap-2 text-sm pt-1">
                    <input
                      type="checkbox"
                      checked={!!form.allows_subscription}
                      onChange={(e) => setForm((f) => ({ ...f, allows_subscription: e.target.checked }))}
                      data-testid="product-form-allows-sub"
                    />
                    Allow subscription option
                  </label>
                </div>

                <DialogFooter>
                  <Button type="submit" className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="product-form-save">
                    {editing ? "Update" : "Create"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="text-emerald-700">Loading…</div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Tags</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.map((p) => (
                  <TableRow key={p.id} data-testid={`product-row-${p.id}`}>
                    <TableCell className="font-medium text-emerald-950">{p.name}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(p.tags || []).slice(0, 5).map((t) => (
                          <span key={t} className="text-[10px] bg-emerald-50 text-emerald-800 rounded-full px-2 py-0.5">
                            {t}
                          </span>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-emerald-800">{p.price || "—"}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="rounded-full"
                        onClick={() => openEdit(p)}
                        data-testid={`product-edit-${p.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="rounded-full text-red-600 hover:text-red-700"
                        onClick={() => remove(p)}
                        data-testid={`product-delete-${p.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/users");
        setUsers(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <Card className="rounded-2xl border-emerald-100" data-testid="admin-users-card">
      <CardContent className="p-5">
        <h2 className="font-display text-lg text-emerald-950 mb-4">All users</h2>
        {loading ? (
          <div className="text-emerald-700">Loading…</div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Behavior</TableHead>
                  <TableHead>BMI</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Conditions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                    <TableCell className="font-medium text-emerald-950">{u.name}</TableCell>
                    <TableCell className="text-emerald-900/80">{u.email}</TableCell>
                    <TableCell>
                      <span className={`text-[10px] rounded-full px-2 py-0.5 ${
                        u.role === "admin" ? "bg-amber-100 text-amber-800" : "bg-emerald-50 text-emerald-800"
                      }`}>
                        {u.role}
                      </span>
                    </TableCell>
                    <TableCell className="text-emerald-900/80 capitalize">
                      {(u.behavior_type || "—").replace("_", " ")}
                    </TableCell>
                    <TableCell className="text-emerald-900">{u.latest_assessment?.bmi ?? "—"}</TableCell>
                    <TableCell className="text-emerald-900">{u.latest_assessment?.bmi_category ?? "—"}</TableCell>
                    <TableCell className="text-emerald-900/80 text-xs">
                      {(u.latest_assessment?.conditions || []).join(", ") || "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


function CouponsTab() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyCoupon);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/coupons");
      setCoupons(data);
    } catch {
      toast.error("Failed to load coupons");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/coupons", {
        ...form,
        value: Number(form.value),
        max_redemptions: Number(form.max_redemptions) || 0,
      });
      toast.success("Coupon created");
      setOpen(false);
      setForm(emptyCoupon);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete coupon ${c.code}?`)) return;
    try {
      await api.delete(`/coupons/${c.id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <Card className="rounded-2xl border-emerald-100" data-testid="admin-coupons-card">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-lg text-emerald-950">Coupons & promo codes</h2>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="admin-add-coupon-btn">
                <Plus className="w-4 h-4 mr-1" /> New coupon
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="admin-coupon-dialog">
              <DialogHeader>
                <DialogTitle>Create coupon</DialogTitle>
              </DialogHeader>
              <form onSubmit={create} className="space-y-3">
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label>Code (auto-generated if empty)</Label>
                    <Input
                      value={form.code}
                      onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                      className="rounded-xl font-mono"
                      placeholder="SAVE10"
                      data-testid="coupon-form-code"
                    />
                  </div>
                  <div>
                    <Label>Type</Label>
                    <select
                      value={form.type}
                      onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
                      className="mt-1 w-full rounded-xl border border-emerald-200 px-3 py-2"
                      data-testid="coupon-form-type"
                    >
                      <option value="percent">Percent off</option>
                      <option value="fixed_gbp">Fixed GBP off</option>
                    </select>
                  </div>
                  <div>
                    <Label>Value</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={form.value}
                      onChange={(e) => setForm((f) => ({ ...f, value: e.target.value }))}
                      className="rounded-xl"
                      data-testid="coupon-form-value"
                    />
                  </div>
                  <div>
                    <Label>Max redemptions (0 = unlimited)</Label>
                    <Input
                      type="number"
                      value={form.max_redemptions}
                      onChange={(e) => setForm((f) => ({ ...f, max_redemptions: e.target.value }))}
                      className="rounded-xl"
                      data-testid="coupon-form-max"
                    />
                  </div>
                </div>
                <div>
                  <Label>Description</Label>
                  <Input
                    value={form.description}
                    onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                    className="rounded-xl"
                    placeholder="Internal note"
                    data-testid="coupon-form-desc"
                  />
                </div>
                <DialogFooter>
                  <Button type="submit" className="rounded-full bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="coupon-form-save">
                    Create
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="text-emerald-700">Loading…</div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Value</TableHead>
                  <TableHead>Kind</TableHead>
                  <TableHead>Used</TableHead>
                  <TableHead>Max</TableHead>
                  <TableHead>Active</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {coupons.map((c) => (
                  <TableRow key={c.id} data-testid={`coupon-row-${c.id}`}>
                    <TableCell className="font-mono font-semibold text-emerald-950">{c.code}</TableCell>
                    <TableCell>{c.type}</TableCell>
                    <TableCell>{c.value}{c.type === "percent" ? "%" : "£"}</TableCell>
                    <TableCell>
                      <span className={`text-[10px] rounded-full px-2 py-0.5 ${
                        c.kind === "referral" ? "bg-amber-100 text-amber-800" : "bg-emerald-50 text-emerald-800"
                      }`}>
                        {c.kind}
                      </span>
                    </TableCell>
                    <TableCell>{c.used_count}</TableCell>
                    <TableCell>{c.max_redemptions || "∞"}</TableCell>
                    <TableCell>{c.active ? "✓" : "—"}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => remove(c)}
                        className="rounded-full text-red-600 hover:text-red-700"
                        data-testid={`coupon-delete-${c.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ReferralsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/referrals");
        setRows(data);
      } catch {
        toast.error("Failed to load referrals");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const totalReferrals = rows.reduce((acc, r) => acc + (r.used_count || 0), 0);
  const topRows = rows.slice(0, 10);

  return (
    <div className="space-y-6" data-testid="admin-referrals-tab">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="rounded-2xl border-emerald-100">
          <CardContent className="p-5">
            <div className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-semibold">Active referrers</div>
            <div className="font-display text-3xl text-emerald-950 mt-2">{rows.length}</div>
          </CardContent>
        </Card>
        <Card className="rounded-2xl border-emerald-100">
          <CardContent className="p-5">
            <div className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-semibold">Total referred sales</div>
            <div className="font-display text-3xl text-emerald-950 mt-2">{totalReferrals}</div>
          </CardContent>
        </Card>
      </div>

      <Card className="rounded-2xl border-emerald-100">
        <CardContent className="p-5">
          <h2 className="font-display text-lg text-emerald-950 mb-4">Top referrers</h2>
          {loading ? (
            <div className="text-emerald-700">Loading…</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead>Successful referrals</TableHead>
                    <TableHead>Bonus credits</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {topRows.map((r) => (
                    <TableRow key={r.code} data-testid={`referral-row-${r.code}`}>
                      <TableCell className="font-medium text-emerald-950">{r.owner?.name || "—"}</TableCell>
                      <TableCell className="text-emerald-900/80">{r.owner?.email || "—"}</TableCell>
                      <TableCell className="font-mono">{r.code}</TableCell>
                      <TableCell>{r.used_count}</TableCell>
                      <TableCell>{r.owner?.bonus_credits || 0}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function CheckinsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/checkins");
        setRows(data);
      } catch {
        toast.error("Failed to load check-ins");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <Card className="rounded-2xl border-emerald-100" data-testid="admin-checkins-card">
      <CardContent className="p-5">
        <h2 className="font-display text-lg text-emerald-950 mb-4">Engagement: weekly check-ins</h2>
        {loading ? (
          <div className="text-emerald-700">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-emerald-900/60 py-4">No check-ins yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Check-ins</TableHead>
                  <TableHead>Avg energy</TableHead>
                  <TableHead>Avg mood</TableHead>
                  <TableHead>Last</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r, i) => (
                  <TableRow key={i} data-testid={`checkin-row-${i}`}>
                    <TableCell className="font-medium text-emerald-950">{r.user?.name || "—"}</TableCell>
                    <TableCell className="text-emerald-900/80">{r.user?.email || "—"}</TableCell>
                    <TableCell>{r.count}</TableCell>
                    <TableCell>{r.avg_energy}/10</TableCell>
                    <TableCell>{r.avg_mood}/10</TableCell>
                    <TableCell className="text-emerald-900/70 text-xs">
                      {r.last_at ? new Date(r.last_at).toLocaleString() : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
